import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './style.scss';
import { NavbarItemLink, NavbarSize } from '../navbar/navbar-item/interface';
import NavbarItem from '../navbar/navbar-item';
import UQIcon from '../icon';
import { UQTooltip } from '../tooltip';
import UQMenu, { UQMenuItem } from '../menu';

interface UQNavbarProps {
  links: NavbarItemLink[];
  logoLight?: string;
  logoDark?: string;
  logoLightSmall?: string;
  logoDarkSmall?: string;
  footerLinks?: NavbarItemLink[];
  singleCollapse?: boolean;
  content?: React.ReactNode;
  location: {
    pathname: string;
  };
  renderLink?: (link: NavbarItemLink, children: React.ReactNode) => React.ReactNode;
  size?: NavbarSize;
  onMobileToggle?: (isOpen: boolean) => void;
  isMobileOpen?: boolean;
  horizontalCollapsed?: boolean;
}

const UQNavbar: React.FC<UQNavbarProps> = ({ 
  links, 
  logoLight, 
  logoDark, 
  logoLightSmall,
  logoDarkSmall,
  footerLinks, 
  singleCollapse, 
  content, 
  location, 
  renderLink,
  size = 'normal',
  onMobileToggle,
  isMobileOpen = false,
  horizontalCollapsed = false
}) => {
  const navigate = useNavigate();
  const [collapsedItems, setCollapsedItems] = useState<{ [key: string]: boolean }>({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(isMobileOpen);
  const [openDropdownMenu, setOpenDropdownMenu] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Theme detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(isMobileOpen);
  }, [isMobileOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownMenu && !event.target) return;
      
      const target = event.target as Element;
      const isDropdownClick = target.closest('.navbar-collapsed-dropdown');
      
      if (!isDropdownClick) {
        setOpenDropdownMenu(null);
      }
    };

    if (openDropdownMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdownMenu]);

  const handleMobileToggle = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    onMobileToggle?.(newState);
  };

  useEffect(() => {
    const initialCollapsedItems = links.reduce((acc, link) => {
      acc[link.url] = true;
      return acc;
    }, {} as { [key: string]: boolean });
    setCollapsedItems(initialCollapsedItems);
  }, [links]);

  const handleCollapseChange = (clickedUrl: string) => {
    setCollapsedItems((prevItems) => ({
      ...prevItems,
      [clickedUrl]: !prevItems[clickedUrl],
      ...(singleCollapse &&
        Object.keys(prevItems).reduce((acc, key) => {
          if (key !== clickedUrl) acc[key] = true;
          return acc;
        }, {} as { [key: string]: boolean })),
    }));
  };

  const convertToMenuItems = (links: NavbarItemLink[]): UQMenuItem[] => {
    return links.map(link => ({
      text: link.text,
      icon: link.icon,
      url: link.url,
      disabled: link.disabled,
      subItems: link.subLevel ? convertToMenuItems(link.subLevel) : undefined
    }));
  };

  const handleCollapsedMenuClick = (item: UQMenuItem) => {
    console.log('Menu item clicked:', item);
    if (item.url) {
      // Close the dropdown first
      setOpenDropdownMenu(null);
      
      console.log('Navigating to:', item.url);
      // Navigate using React Router
      navigate(item.url);
    } else {
      console.log('No URL found for item:', item);
    }
  };

  const handleCollapsedItemClick = (link: NavbarItemLink, event: React.MouseEvent) => {
    if (link.subLevel && link.subLevel.length > 0) {
      const isOpening = openDropdownMenu !== link.url;
      
      if (isOpening) {
        // Calculate position relative to viewport
        const button = event.currentTarget as HTMLElement;
        const rect = button.getBoundingClientRect();
        setDropdownPosition({
          top: rect.top,
          left: rect.right + 10, // 10px gap from button
        });
      }
      
      setOpenDropdownMenu(isOpening ? link.url : null);
    } else if (renderLink) {
      renderLink(link, null);
    }
  };

  const getPreferredLogo = () => {
    const isSmallMode = horizontalCollapsed || size === 'mobile';
    
    if (isSmallMode) {
      if (isDarkMode) {
        return logoDarkSmall || logoDark || logoLightSmall || logoLight;
      } else {
        return logoLightSmall || logoLight || logoDarkSmall || logoDark;
      }
    } else {
      if (isDarkMode) {
        return logoDark || logoLight;
      } else {
        return logoLight || logoDark;
      }
    }
  };

  const preferredLogo = getPreferredLogo();

  return (
    <div className={`layout-base layout-base--${size} ${horizontalCollapsed ? 'layout-base--horizontal-collapsed' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {size === 'mobile' && (
        <div className="mobile-header">
          <div className="mobile-logo">
            {preferredLogo && <img src={preferredLogo} alt="Logo" />}
          </div>
          <button 
            className="mobile-menu-toggle"
            onClick={handleMobileToggle}
            aria-label="Toggle navigation menu"
          >
            <UQIcon icon={isMobileMenuOpen ? "x" : "menu-01"} />
          </button>
        </div>
      )}
      
      <div className={`layout-nav ${size === 'mobile' ? (isMobileMenuOpen ? 'layout-nav--mobile-open' : 'layout-nav--mobile-closed') : ''} ${horizontalCollapsed ? 'layout-nav--horizontal-collapsed' : ''}`}>
        <div className={`navbar-base navbar-base--${size} ${horizontalCollapsed ? 'navbar-base--horizontal-collapsed' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          {size !== 'mobile' && (
            <div className="navbar-logo">
              {preferredLogo && (
                horizontalCollapsed ? (
                  <UQTooltip 
                    content="Dashboard - Navigate to the main dashboard to view your overview, statistics, and recent activity" 
                    position="right" 
                    usePortal={true}
                    interactive={true}
                    hideDelay={500}
                    maxHeight={200}
                    disabled={!!openDropdownMenu}
                  >
                    <img src={preferredLogo} alt="Logo" className="navbar-logo--small" />
                  </UQTooltip>
                ) : (
                  <img src={preferredLogo} alt="Logo" />
                )
              )}
            </div>
          )}
          <div className="navbar-list">
            {links.map((link) => (
              <div key={link.url} className={`navbar-item ${horizontalCollapsed ? 'navbar-item--horizontal-collapsed' : ''}`}>
                {horizontalCollapsed ? (
                  link.subLevel && link.subLevel.length > 0 ? (
                    // Use UQMenu for items with subitems in horizontal collapsed mode
                    <div className="navbar-collapsed-dropdown">
                      <UQTooltip content={link.text} position="right" usePortal={true} disabled={!!openDropdownMenu}>
                        <button
                          className={`navbar-collapsed-button ${openDropdownMenu === link.url ? 'open' : ''}`}
                          onClick={(e) => handleCollapsedItemClick(link, e)}
                        >
                          <div className={`navbar-icon navbar-icon--${size} navbar-icon--horizontal-collapsed`}>
                            {link.icon ? <UQIcon icon={link.icon} /> : null}
                          </div>
                        </button>
                      </UQTooltip>
                    </div>
                  ) : (
                    // Regular tooltip for items without subitems
                    <UQTooltip content={link.text} position="right" usePortal={true} disabled={!!openDropdownMenu}>
                      <NavbarItem
                        link={link}
                        isCollapsed={collapsedItems[link.url] ?? true}
                        onCollapseChange={handleCollapseChange}
                        location={location}
                        renderLink={renderLink}
                        size={size}
                        horizontalCollapsed={horizontalCollapsed}
                      />
                    </UQTooltip>
                  )
                ) : (
                  <NavbarItem
                    link={link}
                    isCollapsed={collapsedItems[link.url] ?? true}
                    onCollapseChange={handleCollapseChange}
                    location={location}
                    renderLink={renderLink}
                    size={size}
                    horizontalCollapsed={horizontalCollapsed}
                  />
                )}
              </div>
            ))}
          </div>
          {footerLinks && footerLinks.length > 0 && (
            <div className="navbar-footer">
              {footerLinks.map((link) => (
                horizontalCollapsed ? (
                  <UQTooltip key={link.url} content={link.text} position="right" usePortal={true} disabled={!!openDropdownMenu}>
                    <div>
                      <NavbarItem
                        link={link}
                        isCollapsed={collapsedItems[link.url] ?? true}
                        onCollapseChange={handleCollapseChange}
                        location={location}
                        renderLink={renderLink}
                        size={size}
                        horizontalCollapsed={horizontalCollapsed}
                      />
                    </div>
                  </UQTooltip>
                ) : (
                  <NavbarItem
                    key={link.url}
                    link={link}
                    isCollapsed={collapsedItems[link.url] ?? true}
                    onCollapseChange={handleCollapseChange}
                    location={location}
                    renderLink={renderLink}
                    size={size}
                    horizontalCollapsed={horizontalCollapsed}
                  />
                )
              ))}
            </div>
          )}
        </div>
      </div>
      
      {size === 'mobile' && isMobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={handleMobileToggle}
        />
      )}
      
      <div className="layout-content">{content}</div>
      
      {/* Portal-based dropdown menu */}
      {horizontalCollapsed && openDropdownMenu && dropdownPosition && (
        createPortal(
          <div 
            className="navbar-dropdown-portal"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
            }}
          >
            <UQMenu 
              items={(() => {
                const activeLink = links.find(link => link.url === openDropdownMenu);
                return activeLink?.subLevel ? convertToMenuItems(activeLink.subLevel) : [];
              })()} 
              onItemClick={handleCollapsedMenuClick}
              className="navbar-horizontal-menu"
            />
          </div>,
          document.body
        )
      )}
    </div>
  );
};

export default UQNavbar;
